// Admin page — selects a tab via ?filter=…
import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { PageHeader } from "../../shared/components/PageHeader";
import { useAuth } from "../../shared/stores/auth";
import { AdminOverview } from "./tabs/Overview";
import { AdminUsers } from "./tabs/Users";
import { AdminDepartments } from "./tabs/Departments";
import { AdminRoles } from "./tabs/Roles";
import { AdminPolicies } from "./tabs/Policies";
import { AdminAudit } from "./tabs/Audit";
import { AdminAi } from "./tabs/Ai";
import { AdminBackup } from "./tabs/Backup";
import { AdminKms } from "./tabs/Kms";
import { AdminIntegrations } from "./tabs/Integrations";
import { AdminWebhooks } from "./tabs/Webhooks";
import { AdminHealth } from "./tabs/Health";
import { AdminApprovalForms } from "./tabs/ApprovalForms";
import { AdminLicense } from "./tabs/License";
import { AdminMenuPermissions } from "./tabs/MenuPermissions";
import { AdminMailDomain } from "./tabs/MailDomain";

const SUBTITLES: Record<string, string> = {
  overview: "관리자 개요",
  "menu-perms": "메뉴 권한 관리",
  users: "사용자",
  departments: "부서/조직",
  roles: "역할/권한",
  policies: "보안 정책",
  audit: "감사 로그",
  ai: "AI 모델",
  backup: "백업/복원",
  kms: "KMS 회전",
  integrations: "통합/API",
  webhooks: "Webhooks",
  health: "시스템 상태",
  forms: "결재 양식 빌더",
  license: "라이선스",
  "mail-domain": "메일 도메인 설정",
};

// Each tab → the menu key that gates it.
// "menu-perms" is open to 실장+ (menu_admin); everything else is SUPER_ADMIN (system_admin).
const TAB_MENU_KEY: Record<string, string> = {
  overview: "system_admin",
  "menu-perms": "menu_admin",
  users: "system_admin",
  departments: "system_admin",
  roles: "system_admin",
  policies: "system_admin",
  audit: "system_admin",
  ai: "system_admin",
  backup: "system_admin",
  kms: "system_admin",
  integrations: "system_admin",
  webhooks: "system_admin",
  health: "system_admin",
  forms: "system_admin",
  license: "system_admin",
  "mail-domain": "system_admin",
};

export default function AdminPage() {
  const [params, setParams] = useSearchParams();
  const manifest = useAuth((s) => s.manifest);
  const canTab = (tabId: string) => manifest?.menus?.[TAB_MENU_KEY[tabId] ?? "system_admin"] === true;

  const requested = params.get("filter") || "overview";
  // The first tab the user is actually allowed to see (overview → menu-perms → …).
  const firstAllowed = Object.keys(TAB_MENU_KEY).find(canTab) ?? "overview";
  const tab = canTab(requested) ? requested : firstAllowed;

  // If the URL points at a tab the user can't see, rewrite it to the allowed one.
  useEffect(() => {
    if (!canTab(requested) && manifest) {
      setParams((p) => { const n = new URLSearchParams(p); n.set("filter", firstAllowed); return n; }, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requested, manifest]);

  return (
    <div style={{ maxWidth: 1180, margin: "0 auto", width: "100%", paddingInline: "clamp(8px, 3vw, 32px)", boxSizing: "border-box" }}>
      <PageHeader title="시스템 관리" subtitle={SUBTITLES[tab] ?? "관리자 전용"} />
      {tab === "overview" && <AdminOverview/>}
      {tab === "menu-perms" && <AdminMenuPermissions/>}
      {tab === "users" && <AdminUsers/>}
      {tab === "departments" && <AdminDepartments/>}
      {tab === "roles" && <AdminRoles/>}
      {tab === "policies" && <AdminPolicies/>}
      {tab === "audit" && <AdminAudit/>}
      {tab === "ai" && <AdminAi/>}
      {tab === "backup" && <AdminBackup/>}
      {tab === "kms" && <AdminKms/>}
      {tab === "integrations" && <AdminIntegrations/>}
      {tab === "webhooks" && <AdminWebhooks/>}
      {tab === "health" && <AdminHealth/>}
      {tab === "forms" && <AdminApprovalForms/>}
      {tab === "license" && <AdminLicense/>}
      {tab === "mail-domain" && <AdminMailDomain/>}
    </div>
  );
}
